import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Loader2, Play, Pause, FileDown, Film, CheckCircle2,
  Scissors, Volume2, Download, Trash2, ChevronUp, ChevronDown, Layers,
  FileCode, Captions, Music, Share2, SlidersHorizontal, ThumbsUp, MessageCircleWarning,
} from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const fmt = (s) => {
  s = Math.max(0, s || 0);
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const toneColor = {
  drama: "#FF2E93", suspense: "#8B5CF6", comédia: "#F59E0B",
  comedia: "#F59E0B", romance: "#00E5FF", treta: "#EF4444",
};

export default function CutEditor() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isProducer = user?.role === "producer";
  const videoRef = useRef(null);
  const pendingSeek = useRef(null);
  const [cut, setCut] = useState(null);
  const [videos, setVideos] = useState({});
  const [activeVid, setActiveVid] = useState(null);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [exports, setExports] = useState([]);
  const [busy, setBusy] = useState(null);
  const [cc, setCc] = useState(true);
  const [trilha, setTrilha] = useState(true);
  const [musicVol, setMusicVol] = useState(18);
  const [stingerVol, setStingerVol] = useState(100);

  const load = async () => {
    const { data } = await api.get(`/cuts/${cid}`);
    setCut(data.cut);
    const map = {};
    data.videos.forEach((v) => { map[v.id] = v; });
    setVideos(map);
    if (!activeVid && data.cut.moments?.length) setActiveVid(data.cut.moments[0].video_id);
    api.get(`/cuts/${cid}/exports`).then((r) => setExports(r.data)).catch(() => setExports([]));
  };
  useEffect(() => { load(); }, [cid]);

  const loadExports = () => api.get(`/cuts/${cid}/exports`).then((r) => setExports(r.data)).catch(() => {});

  const playMoment = (m) => {
    if (m.video_id === activeVid && videoRef.current) {
      videoRef.current.currentTime = m.start;
      videoRef.current.play();
    } else {
      pendingSeek.current = m.start;
      setActiveVid(m.video_id);
    }
  };

  const onLoaded = () => {
    if (pendingSeek.current != null && videoRef.current) {
      videoRef.current.currentTime = pendingSeek.current;
      videoRef.current.play();
      pendingSeek.current = null;
    }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) videoRef.current.play();
    else videoRef.current.pause();
  };

  const updateMoment = (i, key, value) => {
    const moments = cut.moments.map((m, idx) => (idx === i ? { ...m, [key]: value } : m));
    setCut({ ...cut, moments });
  };

  const moveMoment = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= cut.moments.length) return;
    const moments = [...cut.moments];
    [moments[i], moments[j]] = [moments[j], moments[i]];
    setCut({ ...cut, moments });
  };

  const removeMoment = (i) => {
    if (!window.confirm("Excluir esta cena do episódio?")) return;
    setCut({ ...cut, moments: cut.moments.filter((_, idx) => idx !== i) });
  };

  const save = async () => {
    try {
      await api.put(`/cuts/${cid}`, { moments: cut.moments, teaser: cut.teaser });
      toast.success("Episódio salvo");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const approve = async () => {
    try {
      await api.post(`/cuts/${cid}/approve`);
      setCut({ ...cut, status: "approved", approved_by: user.name });
      toast.success("Episódio aprovado");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const roughcut = async () => {
    setBusy("roughcut");
    toast.info("Renderizando o episódio... isso pode levar alguns minutos.");
    try {
      const { data } = await api.post(`/cuts/${cid}/roughcut`, {
        subtitles: cc, soundtrack: trilha,
        music_volume: musicVol / 100, stinger_volume: stingerVol / 100,
      });
      const extras = [data.meta?.subtitles && "legendas", data.meta?.soundtrack && "trilha de tensão"].filter(Boolean).join(" + ");
      const mode = data.meta?.mode === "copy" ? "qualidade original preservada" : "1080p";
      toast.success(`Episódio renderizado (${data.meta.clips} cenas, ${data.meta.duration}s) — ${extras ? extras + ", " : ""}${mode}`);
      await loadExports();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const narration = async () => {
    setBusy("narration");
    try {
      await api.post(`/cuts/${cid}/narration`);
      toast.success("Narração em voz gerada");
      await loadExports();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const share = async () => {
    try {
      const { data } = await api.post(`/cuts/${cid}/share`);
      const url = `${window.location.origin}/share/${data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link de prévia copiado! Envie para o cliente — não precisa de conta.");
      } catch {
        window.prompt("Copie o link de prévia:", url);
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (!cut) return <div className="h-screen flex items-center justify-center bg-[#08080C]"><Loader2 className="w-6 h-6 animate-spin text-[#FF2E93]" /></div>;

  const src = activeVid ? `${API}/videos/${activeVid}/stream` : null;

  return (
    <div className="h-screen flex flex-col bg-[#08080C] text-[#F4F4F5]">
      <div className="grain" />
      <header className="h-14 border-b border-[#282834] flex items-center px-4 gap-4 shrink-0 z-10">
        <button onClick={() => navigate(`/projects/${cut.project_id}`)} data-testid="cut-back" className="text-[#A1A1AA] hover:text-[#F4F4F5] flex items-center gap-2 text-sm transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <span className="text-sm truncate flex items-center gap-2">
          <Layers className="w-4 h-4 text-[#FF2E93]" />
          <span className="font-semibold">{cut.title}</span>
          <span className="text-[#555] text-xs">· {cut.video_ids?.length} vídeos de origem</span>
          {cut.client_approval && (
            <span data-testid="client-approval-badge"
              title={`${cut.client_approval.author}${cut.client_approval.comment ? `: ${cut.client_approval.comment}` : ""}`}
              className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 border ${cut.client_approval.status === "approved" ? "bg-[#10B981]/15 border-[#10B981]/40 text-[#10B981]" : "bg-[#F59E0B]/15 border-[#F59E0B]/40 text-[#F59E0B]"}`}>
              {cut.client_approval.status === "approved"
                ? <><ThumbsUp className="w-3 h-3" /> Cliente aprovou</>
                : <><MessageCircleWarning className="w-3 h-3" /> Cliente pediu ajustes</>}
            </span>
          )}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={share} data-testid="cut-share-button" title="Gerar link público de prévia para o cliente"
            className="text-xs rounded-full px-3 py-2 flex items-center gap-1.5 border bg-[#1C1C24] border-[#282834] hover:border-[#F59E0B] text-[#F4F4F5] transition-colors duration-200">
            <Share2 className="w-3.5 h-3.5 text-[#F59E0B]" /> Compartilhar
          </button>
          <button onClick={() => setCc(!cc)} data-testid="cut-toggle-subtitles" title="Legendas estilizadas queimadas no episódio"
            className={`text-xs rounded-full px-3 py-2 flex items-center gap-1.5 border transition-colors duration-200 ${cc ? "bg-[#00E5FF]/15 border-[#00E5FF] text-[#00E5FF]" : "bg-[#1C1C24] border-[#282834] text-[#555]"}`}>
            <Captions className="w-3.5 h-3.5" /> Legendas
          </button>
          <button onClick={() => setTrilha(!trilha)} data-testid="cut-toggle-soundtrack" title="Trilhas do projeto (ou stingers automáticos) nas cenas de treta/drama/suspense"
            className={`text-xs rounded-full px-3 py-2 flex items-center gap-1.5 border transition-colors duration-200 ${trilha ? "bg-[#8B5CF6]/15 border-[#8B5CF6] text-[#8B5CF6]" : "bg-[#1C1C24] border-[#282834] text-[#555]"}`}>
            <Music className="w-3.5 h-3.5" /> Trilha
          </button>
          {trilha && (
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="cut-volume-settings" title="Ajustar volumes da trilha e dos stingers"
                  className="text-xs rounded-full px-2.5 py-2 flex items-center gap-1 border bg-[#1C1C24] border-[#282834] hover:border-[#8B5CF6] text-[#8B5CF6] transition-colors duration-200">
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64 bg-[#121218] border-[#282834] text-[#F4F4F5] p-4" align="end">
                <p className="text-xs font-semibold mb-3 flex items-center gap-1.5"><Music className="w-3.5 h-3.5 text-[#8B5CF6]" /> Volumes da prévia</p>
                <label className="text-[11px] text-[#A1A1AA] flex justify-between mb-1">
                  Trilha de fundo <span className="text-[#8B5CF6] font-mono">{musicVol}%</span>
                </label>
                <input type="range" min="0" max="100" value={musicVol} onChange={(e) => setMusicVol(+e.target.value)}
                  data-testid="music-volume-slider" className="w-full accent-[#8B5CF6] mb-3" />
                <label className="text-[11px] text-[#A1A1AA] flex justify-between mb-1">
                  Stingers (impactos) <span className="text-[#F59E0B] font-mono">{stingerVol}%</span>
                </label>
                <input type="range" min="0" max="100" value={stingerVol} onChange={(e) => setStingerVol(+e.target.value)}
                  data-testid="stinger-volume-slider" className="w-full accent-[#F59E0B]" />
                <p className="text-[10px] text-[#555] mt-3">Aplicado no próximo Rough Cut. 0% desliga o elemento.</p>
              </PopoverContent>
            </Popover>
          )}
          <span className="w-px h-5 bg-[#282834]" />
          <a href={`${API}/cuts/${cid}/edl`} data-testid="cut-edl-button"
            className="text-sm rounded-full px-4 py-2 flex items-center gap-2 bg-[#1C1C24] border border-[#282834] hover:border-[#00E5FF] text-[#F4F4F5] transition-colors duration-200">
            <FileDown className="w-4 h-4" /> EDL
          </a>
          <a href={`${API}/cuts/${cid}/xml`} data-testid="cut-xml-button" title="Timeline XML para Premiere/DaVinci"
            className="text-sm rounded-full px-4 py-2 flex items-center gap-2 bg-[#1C1C24] border border-[#282834] hover:border-[#00E5FF] text-[#F4F4F5] transition-colors duration-200">
            <FileCode className="w-4 h-4" /> XML
          </a>
          <button onClick={narration} disabled={busy} data-testid="cut-narration-button"
            className="text-sm rounded-full px-4 py-2 flex items-center gap-2 bg-[#1C1C24] border border-[#282834] hover:border-[#00E5FF] text-[#F4F4F5] transition-colors duration-200 disabled:opacity-40">
            {busy === "narration" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4 text-[#00E5FF]" />} Narração
          </button>
          <button onClick={roughcut} disabled={busy} data-testid="cut-roughcut-button"
            className="text-sm rounded-full px-4 py-2 flex items-center gap-2 bg-[#FF2E93] hover:bg-[#ff54a6] text-white transition-colors duration-200 disabled:opacity-40">
            {busy === "roughcut" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scissors className="w-4 h-4" />} Rough Cut
          </button>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] min-h-0">
        {/* Player + deliveries */}
        <div className="border-r border-[#282834] flex flex-col min-h-0">
          <div className="bg-black flex items-center justify-center relative" style={{ aspectRatio: "16/9" }}>
            {src && videos[activeVid] ? (
              <video ref={videoRef} src={src} className="w-full h-full object-contain" data-testid="cut-player" preload="metadata"
                onTimeUpdate={(e) => setCurrent(e.target.currentTime)}
                onLoadedMetadata={onLoaded}
                onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />
            ) : (
              <p className="text-sm text-[#555]">Sem prévia disponível</p>
            )}
          </div>
          <div className="p-4 flex items-center gap-4 border-b border-[#282834]">
            <button onClick={togglePlay} data-testid="cut-play-toggle" className="w-11 h-11 rounded-full bg-[#FF2E93] hover:bg-[#ff54a6] flex items-center justify-center transition-colors duration-200">
              {playing ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
            </button>
            <span className="font-mono text-sm text-[#A1A1AA]">{fmt(current)}</span>
            {activeVid && videos[activeVid] && (
              <span className="text-xs text-[#555] font-mono truncate ml-auto">Fonte: {videos[activeVid].title}</span>
            )}
          </div>

          <div className="flex-1 p-4 overflow-y-auto min-h-0">
            {exports.length > 0 ? (
              <div data-testid="cut-deliveries-panel">
                <div className="flex items-center gap-2 mb-3">
                  <Download className="w-4 h-4 text-[#FF2E93]" />
                  <h3 className="font-head font-600 text-sm">Entregas do episódio</h3>
                </div>
                <div className="space-y-2">
                  {exports.map((e) => (
                    <div key={e.id} data-testid={`cut-export-${e.type}-${e.id}`} className="bg-[#121218] border border-[#282834] rounded-lg p-3">
                      {e.type === "narration" ? (
                        <div>
                          <p className="text-xs text-[#00E5FF] mb-2 flex items-center gap-1"><Volume2 className="w-3 h-3" /> {e.filename}</p>
                          <audio controls src={`${API}/exports/${e.id}/download`} className="w-full h-9" />
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm truncate flex items-center gap-1.5">
                              <Scissors className="w-3.5 h-3.5 text-[#FF2E93]" /> {e.filename}
                            </p>
                            <p className="text-[10px] text-[#A1A1AA]">
                              {(e.size / 1024 / 1024).toFixed(1)} MB{e.meta?.duration ? ` · ${e.meta.duration}s` : ""}
                              {e.meta?.mode === "copy" ? " · sem perda" : e.meta?.mode === "reencode" ? " · 1080p" : ""}
                              {e.meta?.subtitles ? " · legendas" : ""}{e.meta?.soundtrack ? " · trilha" : ""}
                            </p>
                          </div>
                          <a href={`${API}/exports/${e.id}/download?download=1`} data-testid={`cut-download-${e.id}`}
                            className="shrink-0 text-xs bg-[#1C1C24] border border-[#282834] hover:border-[#00E5FF] rounded-full px-3 py-1.5 flex items-center gap-1 transition-colors duration-200">
                            <Download className="w-3 h-3" /> Baixar
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center mt-6">
                <Film className="w-8 h-8 text-[#282834] mx-auto mb-3" />
                <p className="text-sm text-[#555]">Revise as cenas ao lado, salve e clique em Rough Cut para renderizar o episódio.</p>
              </div>
            )}
          </div>
        </div>

        {/* Script / moments */}
        <div className="flex flex-col min-h-0">
          <div className="p-4 border-b border-[#282834] flex items-center justify-between">
            <h3 className="font-head font-600 text-sm">Roteiro do episódio</h3>
            <span className="text-xs text-[#555]">{cut.moments.length} cenas</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 min-h-0 space-y-4" data-testid="cut-script-panel">
            {cut.status === "approved" && (
              <div className="flex items-center gap-2 text-xs text-[#10B981] bg-[#10B981]/10 rounded-lg px-3 py-2">
                <CheckCircle2 className="w-4 h-4" /> Aprovado por {cut.approved_by}
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#00E5FF]">Chamada de abertura</label>
              <textarea value={cut.teaser} onChange={(e) => setCut({ ...cut, teaser: e.target.value })}
                data-testid="cut-teaser"
                className="mt-1 w-full bg-[#121218] border border-[#282834] rounded-lg p-3 text-sm focus:outline-none focus:border-[#FF2E93] transition-colors duration-200 resize-none h-20" />
            </div>
            {cut.resumo_editorial && (
              <p className="text-sm text-[#A1A1AA] italic border-l-2 border-[#282834] pl-3">{cut.resumo_editorial}</p>
            )}

            <div className="space-y-3">
              {cut.moments.map((m, i) => (
                <div key={i} data-testid={`cut-moment-${i}`} className="bg-[#121218] border border-[#282834] rounded-lg p-3" style={{ borderLeft: `3px solid ${toneColor[m.tone] || "#FF2E93"}` }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono text-[#555] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <input value={m.title} onChange={(e) => updateMoment(i, "title", e.target.value)}
                      className="bg-transparent text-sm font-medium flex-1 min-w-0 focus:outline-none" />
                    <button onClick={() => playMoment(m)} data-testid={`cut-play-moment-${i}`}
                      className="text-[10px] font-mono text-[#00E5FF] hover:text-[#FF2E93] transition-colors duration-200 shrink-0">
                      ▶ {fmt(m.start)}→{fmt(m.end)}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider" style={{ color: toneColor[m.tone] || "#FF2E93" }}>{m.tone}</span>
                    <span className="text-[10px] text-[#555] font-mono truncate">· {videos[m.video_id]?.title || "?"}</span>
                    <span className="ml-auto flex items-center gap-1">
                      <button onClick={() => moveMoment(i, -1)} disabled={i === 0} data-testid={`cut-moment-up-${i}`} className="text-[#555] hover:text-[#F4F4F5] disabled:opacity-30 transition-colors duration-200"><ChevronUp className="w-4 h-4" /></button>
                      <button onClick={() => moveMoment(i, 1)} disabled={i === cut.moments.length - 1} data-testid={`cut-moment-down-${i}`} className="text-[#555] hover:text-[#F4F4F5] disabled:opacity-30 transition-colors duration-200"><ChevronDown className="w-4 h-4" /></button>
                      <button onClick={() => removeMoment(i)} data-testid={`cut-moment-delete-${i}`} className="text-[#555] hover:text-[#EF4444] transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                    </span>
                  </div>
                  <textarea value={m.narration} onChange={(e) => updateMoment(i, "narration", e.target.value)}
                    data-testid={`cut-moment-narration-${i}`}
                    className="mt-2 w-full bg-[#08080C] border border-[#282834] rounded p-2 text-xs text-[#F4F4F5] focus:outline-none focus:border-[#FF2E93] transition-colors duration-200 resize-none h-16" />
                </div>
              ))}
            </div>

            <div className="flex gap-2 pt-2 pb-6">
              <button onClick={save} data-testid="cut-save-button" className="flex-1 bg-[#1C1C24] border border-[#282834] hover:border-[#00E5FF] rounded-lg py-2 text-sm transition-colors duration-200">Salvar</button>
              {isProducer && cut.status !== "approved" && (
                <button onClick={approve} data-testid="cut-approve-button" className="flex-1 bg-[#10B981] hover:bg-[#0ea371] text-white rounded-lg py-2 text-sm transition-colors duration-200 flex items-center justify-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" /> Aprovar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
